import React, { useState, useContext, useEffect } from 'react'
import { z } from 'zod'
import styled from 'styled-components'
import { Button, Modal, Input, Form, notification, Typography } from 'antd'
import { FormattedMessage } from 'react-intl'

import { ParticipantCourseContext } from '../Context/ParticipantCourseContext'
import { AlertTriangle } from 'react-feather'
import { FlexRow } from '../../FlexboxReusables/FlexItems'

// TODO: i18n this whole component

const StartExamModal = () => {
  const {
    isStartExamModalVisible,
    setIsStartExamModalVisible,
    startExam,
    maxExamTriesExceeded,
    maxExamParticipantsExceeded,
    originalLinkForParticipants
  } = useContext(ParticipantCourseContext)

  const [ showInputFields, setShowInputFields ] = useState(false)
  const [ firstName, setFirstName ] = useState('')
  const [ lastName, setLastName ] = useState('')
  const [ birthDate, setBirthDate ] = useState('')
  const [ email, setEmail ] = useState('')

  useEffect(() => {
    const userData = localStorage.getItem('examUserData')

    if (userData) {
      const { firstName, lastName, birthDate, email } = JSON.parse(userData)
      setFirstName(firstName)
      setLastName(lastName)
      setBirthDate(birthDate)
      setEmail(email)
    }
  }, [])

  /**
   * @description avoids leading/trailing whitespaces in Database
   */
  const trimInput = (input: string) => input.trim()

  const handleConfirmClick = () => {
    const trimmedFirstName = trimInput(firstName)
    const trimmedLastName = trimInput(lastName)
    const trimmedEmail = trimInput(email)
  
    const inputValidation = userInputSchema.safeParse({
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      birthDate,
      email: trimmedEmail
    })
  
    if (inputValidation.success) {
      localStorage.setItem('examUserData', JSON.stringify({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        birthDate,
        email: trimmedEmail
      }))
  
      startExam({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        birthDate,
        email: trimmedEmail,
        originalLinkForParticipants
      })
    } else {
      notification.error({
        message: 'Validierungsfehler',
        description: (
          <ul>
            {inputValidation.error.errors.map((err, index) => (
              <li key={index}>{err.message}</li>
            ))}
          </ul>
        ),
        placement: 'bottomRight',
      })
    }
  }

  const clearUserData = () => {
    setFirstName('')
    setLastName('')
    setBirthDate('')
    localStorage.removeItem('examUserData')
  }

  const userHasDataInLocalStorage = () => !!localStorage.getItem('examUserData')

  const renderModalsConditionally = () => {
    if (maxExamTriesExceeded) {
      return renderErrorModal('Versuchen')
    }

    if (maxExamParticipantsExceeded) {
      return renderErrorModal('Prüfungsvorgängen')
    }

    return renderStartExamModal()
  }

  const renderStartExamModal = () => {
    return (
      <Modal
        centered
        title={<FormattedMessage id='exam.startExamModal.title' />}
        open={isStartExamModalVisible}
        onCancel={() => setIsStartExamModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsStartExamModalVisible(false)}>
            <FormattedMessage id='generic.cancel' />
          </Button>,
          !showInputFields ? (
            <Button key="start" type="primary" onClick={() => setShowInputFields(true)}>
              <FormattedMessage id='generic.next' />
            </Button>
          ) : (
            <>
              {userHasDataInLocalStorage() && (
                <Button key="clear" onClick={clearUserData}>
                  Daten zurücksetzen
                </Button>
              )}
              <Button
                key="confirm"
                type="primary"
                onClick={handleConfirmClick}
              >
                <FormattedMessage id='generic.start' />
              </Button>

            </>
          ),
        ]}
      >
        {!showInputFields ? (
          <>
            <p>
              <FormattedMessage id='exam.startExamModal.message1' />
            </p>
            <p>
              <FormattedMessage id='exam.startExamModal.message2' />
            </p>
          </>
        ) : (
          <>
            <FlexRow justifyContent='flex-start' margin='1.5em 0'>
              <Typography.Text>
                Bitte geben Sie Ihre Daten ein, um die Prüfung zu starten.
                <p />
                Sollten Sie die Prüfung wiederholen müssen, müssen Sie bei jedem weiteren Versuch die <strong>exakt</strong> gleichen Daten verwenden.
              </Typography.Text>
            </FlexRow>
            <Form>
              <Form.Item label={<FormattedMessage id='generic.firstName' />} required>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} />
              </Form.Item>
              <Form.Item label={<FormattedMessage id='generic.lastName' />} required>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} />
              </Form.Item>
              <Form.Item label={<FormattedMessage id='generic.birthDate' />} required>
                <Input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
              </Form.Item>
              <EmailInfoText>
                An diese Email-Adresse wird nach erfolgreicher Teilnahme der Link zum (erneuten) herunterladen des Zertifikats geschickt. Wir verwenden Ihre Email-Adresse niemals zu Werbe- oder Kontaktzwecken.
              </EmailInfoText>
              <Form.Item label={'Email-Adresse'} required>
                <Input value={email} onChange={e => setEmail(e.target.value)} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    )    
  }

  return (
    <>
      {renderModalsConditionally()}
    </>
  )
}

const renderErrorModal = (type: 'Prüfungsvorgängen' | 'Versuchen' ) => {
  return (
    <Modal
      centered
      title={(
        <FlexRow>
          <AlertIcon />
          {' '}
          {`Maximale Anzahl an ${type} erreicht.`}
        </FlexRow>
      )}
      open={true}
      footer={null}
      closable={false}
    >
      <p>
        {`Es tut uns Leid, die maximale Anzahl an ${type} wurde bereits erreicht.`}
      </p>
      <p>
        Bitte wenden Sie sich an Ihren Kursleiter.
      </p>
      <p>
        Sie können das Browserfenster bzw. den Tab nun schließen.
      </p>
    </Modal>
  )
}

export default StartExamModal

const AlertIcon = styled(AlertTriangle)`
  color: orange;
  margin-right: 0.5em;
`

const EmailInfoText = styled(Typography.Text)`
  margin-bottom: 1em;
  font-size: 0.8em;
  color: #666;
  display: block;
`

export const userInputSchema = z.object({
  firstName: z.string().min(2, "Der Vorname muss mindestens 2 Zeichen lang sein."),
  lastName: z.string().min(2, "Der Nachname muss mindestens 2 Zeichen lang sein."),
  birthDate: z.string()
    .refine(val => !isNaN(Date.parse(val)), {
      message: "Ungültiges Geburtsdatum. Bitte geben Sie das Datum im Format JJJJ.MM.TT an."
    })
    .transform(val => new Date(val)),
  email: z.string().email("Ungültige Email-Adresse")
})
